<template>
    <DefaultHeader />
    <div class="container-fluid my-3">
        <div class="card shadow bg-white rounded">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Tests véhicules</h5>
                <!-- Sélecteur de balise -->
                <div class="d-flex align-items-center gap-2">
                    <span class="text-muted small d-none d-md-inline">Sélectionner un véhicule :</span>
                    <BaliseSelect :selectedBaliseId="selectedBaliseId" @baliseChange="onBaliseChange" />
                </div>
            </div>

            <div class="card-body">
                <!-- Etat sans sélection -->
                <div v-if="!selectedBaliseId" class="text-muted">
                    Choisis un véhicule pour gérer ses tests.
                </div>

                <!-- Liste + CRUD -->
                <div v-else>
                    <div class="d-flex align-items-end gap-2 mb-3">
                        <div class="flex-grow-1">
                            <label class="form-label">Ajouter un test</label>
                            <input v-model="newLabel" type="text" class="form-control"
                                placeholder="Ex. Vérification extincteur" @keyup.enter="createItem" />
                        </div>
                        <ButtonDisplay :type="'add'" :text="'Ajouter'" :disabled="!newLabel.trim() || loading"
                            @click="createItem" />
                    </div>

                    <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

                    <div class="table-responsive">
                        <table class="table align-middle">
                            <thead>
                                <tr>
                                    <th style="width:60%">Label</th>
                                    <th class="text-end" style="width:40%">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loading">
                                    <td colspan="2" class="text-muted">Chargement…</td>
                                </tr>

                                <tr v-for="it in items" :key="it.id">
                                    <td>
                                        <div v-if="editingId === it.id" class="d-flex gap-2">
                                            <input v-model="editingLabel" class="form-control" :disabled="loading"
                                                @keyup.enter="saveEdit" />
                                        </div>
                                        <div v-else>{{ it.label }}</div>
                                    </td>
                                    <td class="text-end">
                                        <template v-if="editingId === it.id">
                                            <ButtonDisplay :type="'save'" :text="'Enregistrer'"
                                                :disabled="!editingLabel.trim() || loading" @click="saveEdit" />
                                            <ButtonDisplay class="ms-2" :type="'cancel'" :text="'Annuler'"
                                                :disabled="loading" @click="cancelEdit" />
                                        </template>
                                        <template v-else>
                                            <ButtonDisplay :type="'edit'" :text="'Modifier'" :disabled="loading"
                                                @click="startEdit(it)" />
                                            <ButtonDisplay class="ms-2" :type="'delete'" :text="'Supprimer'"
                                                :disabled="loading" @click="removeItem(it)" />
                                        </template>
                                    </td>
                                </tr>

                                <tr v-if="!loading && items.length === 0">
                                    <td colspan="2" class="text-muted">Aucun test pour ce véhicule.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>
        </div>
    </div>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import ButtonDisplay from '@/components/display/ButtonDisplay.vue';
import BaliseSelect from '@/components/display/BaliseSelect.vue';
import MissionPrecheckItemService from '@/services/missionPrecheckItem.service';
import UserHelper from '@/helpers/user.helper.js';

export default {
    name: 'MissionPrecheckAdmin',
    components: { DefaultHeader, DefaultFooter, ButtonDisplay, BaliseSelect },
    data() {
        return {
            selectedBaliseId: null,
            items: [],
            newLabel: '',
            editingId: null,
            editingLabel: '',
            loading: false,
            error: ''
        };
    },
    computed: {
        isViewer() {
            return UserHelper.isViewer(this.$store.state.auth.user);
        }
    },
    methods: {
        onBaliseChange(balise) {
            this.selectedBaliseId = balise?.id || null;
            this.items = [];
            this.newLabel = '';
            this.editingId = null;
            this.editingLabel = '';
            this.error = '';
            if (this.selectedBaliseId) this.fetchItems();
        },
        async fetchItems() {
            try {
                this.loading = true;
                this.error = '';
                const { data } = await MissionPrecheckItemService.list(this.selectedBaliseId);
                this.items = Array.isArray(data) ? data : [];
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erreur de chargement.';
            } finally {
                this.loading = false;
            }
        },
        async createItem() {
            if (!this.selectedBaliseId || !this.newLabel.trim()) return;
            try {
                this.loading = true;
                this.error = '';
                await MissionPrecheckItemService.create(this.selectedBaliseId, this.newLabel.trim());
                this.newLabel = '';
                await this.fetchItems();
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erreur à la création.';
            } finally {
                this.loading = false;
            }
        },
        startEdit(it) {
            this.editingId = it.id;
            this.editingLabel = it.label;
        },
        cancelEdit() {
            this.editingId = null;
            this.editingLabel = '';
        },
        async saveEdit() {
            if (!this.editingId || !this.editingLabel.trim()) return;
            try {
                this.loading = true;
                this.error = '';
                await MissionPrecheckItemService.update(this.editingId, { label: this.editingLabel.trim() });
                this.cancelEdit();
                await this.fetchItems();
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erreur à la mise à jour.';
            } finally {
                this.loading = false;
            }
        },
        async removeItem(it) {
            if (!confirm(`Supprimer "${it.label}" ?`)) return;
            try {
                this.loading = true;
                this.error = '';
                await MissionPrecheckItemService.remove(it.id);
                await this.fetchItems();
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erreur à la suppression.';
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>